import { SeedingStrategy } from "features/WorklistTools/shared/interfaces";
import {
  getColIndexFromSelectedWell,
  getPlateWellIdFromRowAndColIndex,
  getRowIndexFromSelectedWell,
} from "features/WorklistTools/shared/WorklistHelpers";
import { WorklistToolTemplateContentsInput } from "graphql/generated/schema-types";
import {
  DestPlateInfoState,
  ExternalThawToolState,
  IntermediatePlateInfoState,
  SourcePlateInfoState,
  WorklistWellMappingState,
} from "../../state";
import { IExternalThawMethodSettings } from "../../state/initial-state";

export const getSourcePlateInfo = (
  externalThawState: ExternalThawToolState
) => {
  return externalThawState.sourcePlateInfo
    .filter((e) => e.labwareTypeCode !== "")
    .map((item, index) => {
      return {
        plateType: "Source",
        plateIndex: index,
        labwareTypeCode: item.labwareTypeCode,
        numberOfRows: item.rows.length,
        numberOfColumns: item.cols.length,
      };
    });
};

export const getIntPlateInfo = (externalThawState: ExternalThawToolState) => {
  return externalThawState.intPlateInfo
    .filter((e) => e.labwareTypeCode !== "")
    .map((item, index) => {
      return {
        plateType: "Int",
        plateIndex: index,
        labwareTypeCode: item.labwareTypeCode,
        numberOfRows: item.rows.length,
        numberOfColumns: item.cols.length,
        operatingVol: item.operatingVol,
        resuspensionVol: item.resuspensionVol,
      };
    });
};

export const getDestPlateInfo = (externalThawState: ExternalThawToolState) => {
  return externalThawState.destPlateInfo
    .filter((e) => e.labwareTypeCode !== "")
    .map((item, index) => {
      return {
        plateType: "Dest",
        plateIndex: index,
        labwareTypeCode: item.labwareTypeCode,
        numberOfRows: item.rows.length,
        numberOfColumns: item.cols.length,
        operatingVol: item.operatingVol,
        preprocess: item.preprocess,
        topup: item.topup,
        topUpVol: item.topUpVol,
        startsInIncubator: item.startsInIncubator,
        endsInIncubator: item.endsInIncubator,
        minOperatingVol: item.minOperatingVol,
      };
    });
};

export const getDeadPlateInfo = (externalThawState: ExternalThawToolState) => {
  return externalThawState.deadPlateType
    ? [
        {
          plateType: "Dead",
          plateIndex: 0,
          labwareTypeCode: externalThawState.deadPlateType,
          numberOfRows: 0,
          numberOfColumns: 0,
        },
      ]
    : [];
};

export const buildExternalThawTubeTemplate = (
  sourcePlateInfo: SourcePlateInfoState[]
) => {
  const templateContents: WorklistToolTemplateContentsInput[] = [];

  for (const sourcePlate of sourcePlateInfo) {
    if (sourcePlate.plateBarcode === "") {
      continue;
    }
    const sourcePlateType = sourcePlate.labwareTypeCode;

    for (const sourceWellInfo of sourcePlate.wellInfo) {
      const sourceWellId = getPlateWellIdFromRowAndColIndex(
        sourceWellInfo.rowPos - 1,
        sourceWellInfo.colPos - 1
      );

      templateContents.push({
        task: "ExternalThawTube",
        details: `TubeBarcode|${sourceWellInfo.barcode}`,
        aspirateVol: "",
        dispenseVol: "",
        transferVol: "",
        sourcePlateType: sourcePlateType,
        sourcePlatePosition: null,
        sourceWellID: sourceWellId,
        destinationPlateType: "",
        destinationPlatePosition: null,
        destinationWellID: "",
      });
    }
  }
  return templateContents;
};

export const buildIntermediatWellsTemplate = (
  harvestWells: WorklistWellMappingState[],
  sourcePlateInfo: SourcePlateInfoState[],
  intPlateInfo: IntermediatePlateInfoState[]
) => {
  const worklist: WorklistToolTemplateContentsInput[] = [];
  for (const harvestWellMapping of harvestWells) {
    const currentSourcePlate =
      sourcePlateInfo[harvestWellMapping.sourcePlateIndex];
    const intPlate = intPlateInfo[0];
    const index = currentSourcePlate.wellInfo.findIndex(
      (e: any) =>
        e.rowPos - 1 ===
          getRowIndexFromSelectedWell(harvestWellMapping.sourceWellId) &&
        e.colPos - 1 ===
          getColIndexFromSelectedWell(harvestWellMapping.sourceWellId)
    );
    if (index > -1)
      worklist.push({
        task: "IntermediatePlate",
        details: "",
        aspirateVol: "",
        dispenseVol: `${intPlate.resuspensionVol}`,
        transferVol: "",
        sourcePlateType: currentSourcePlate.labwareTypeCode,
        sourcePlatePosition: harvestWellMapping.sourcePlateIndex,
        sourceWellID: harvestWellMapping.sourceWellId,
        destinationPlateType: intPlate.labwareTypeCode,
        destinationPlatePosition: harvestWellMapping.destPlateIndex,
        destinationWellID: harvestWellMapping.destWellId,
      });
  }
  return worklist;
};

export const buildDeadTotalPlateTemplate = (
  methodSettings: IExternalThawMethodSettings,
  deadPlateType: string,
  deadPlateBarcode: string
): WorklistToolTemplateContentsInput => {
  return {
    task: "DeadTotalPlate",
    details: `ExperimentSettings|${methodSettings.experimentSettings};AliquotNumber|${methodSettings.numberOfAliquots};IncubationTime|${methodSettings.incubationTime};`,
    aspirateVol: "",
    dispenseVol: "",
    transferVol: `${methodSettings.aliquotVolume}`,
    sourcePlateType: "",
    sourcePlatePosition: null,
    sourceWellID: "",
    destinationPlateType: deadPlateType,
    destinationPlatePosition: null,
    destinationWellID: "",
  };
};

export const buildSeedingStrategyTemplate = (
  methodSettings: IExternalThawMethodSettings
): WorklistToolTemplateContentsInput => {
  const seedingStrategyString =
    methodSettings.seedingStrategy === SeedingStrategy.CellCount
      ? "Targeted"
      : "Volumetric";

  return {
    task: "SeedingStrategy",
    details: `SeedingStrat|${seedingStrategyString};`,
    aspirateVol: "",
    dispenseVol: "",
    transferVol: "",
    sourcePlateType: "",
    sourcePlatePosition: null,
    sourceWellID: "",
    destinationPlateType: "",
    destinationPlatePosition: null,
    destinationWellID: "",
  };
};

export const buildInt1ToDestTemplate = (
  int1ToDest: WorklistWellMappingState[],
  intPlateInfo: IntermediatePlateInfoState[],
  destPlateInfo: DestPlateInfoState[]
) => {
  const worklist: WorklistToolTemplateContentsInput[] = [];

  for (const destWellMapping of int1ToDest) {
    const destPlateIndex = destPlateInfo.findIndex(
      (item) => item.index === destWellMapping.destPlateIndex
    );
    worklist.push({
      task: "DestinationPlate",
      details: `Preprocess|${+!!destPlateInfo[destPlateIndex]
        .preprocess};Topup|${+!!destPlateInfo[destPlateIndex]
        .topup};StartInIncubator|${+!!destPlateInfo[destPlateIndex]
        .startsInIncubator};EndInIncubator|${+!!destPlateInfo[destPlateIndex]
        .endsInIncubator};TopupVolume|${
        destPlateInfo[destPlateIndex].topup
          ? destPlateInfo[destPlateIndex].topUpVol
          : ""
      };`,
      aspirateVol: "",
      dispenseVol: "",
      transferVol: `${destWellMapping.transferVol ?? 0}`,
      sourcePlateType: intPlateInfo[0].labwareTypeCode,
      sourcePlatePosition: destWellMapping.sourcePlateIndex,
      sourceWellID: destWellMapping.sourceWellId,
      destinationPlateType: destPlateInfo[destPlateIndex].labwareTypeCode,
      destinationPlatePosition: destWellMapping.destPlateIndex,
      destinationWellID: destWellMapping.destWellId,
    });
  }
  return worklist;
};

export const buildIntCentrifugationTemplate = (
  methodSettings: IExternalThawMethodSettings
): WorklistToolTemplateContentsInput => {
  return {
    task: "IntermediateCentrifugation",
    details: `VSpinRPM|${methodSettings.spinParamPercent};VSpinTime|${methodSettings.spinTime};VSpinAccel|${methodSettings.spinAccel};VSpinDecel|${methodSettings.spinDecel};`,
    aspirateVol: "",
    dispenseVol: "",
    transferVol: "",
    sourcePlateType: "",
    sourcePlatePosition: null,
    sourceWellID: "",
    destinationPlateType: "",
    destinationPlatePosition: null,
    destinationWellID: "",
  };
};

export const buildAdditionalMethodSettingsTemplate = (
  methodSettings: IExternalThawMethodSettings
) => {
  const worklist: WorklistToolTemplateContentsInput[] = [];

  worklist.push({
    task: "SplitSeedingVolEvenly",
    details: `${+!!methodSettings.splitSeedingVolEvenly}`,
    aspirateVol: "",
    dispenseVol: "",
    transferVol: "",
    sourcePlateType: "",
    sourcePlatePosition: null,
    sourceWellID: "",
    destinationPlateType: "",
    destinationPlatePosition: null,
    destinationWellID: "",
  });

  worklist.push({
    task: "DefaultVSpinSettings",
    details: `${+!!methodSettings.defaultVSpinSettings}`,
    aspirateVol: "",
    dispenseVol: "",
    transferVol: "",
    sourcePlateType: "",
    sourcePlatePosition: null,
    sourceWellID: "",
    destinationPlateType: "",
    destinationPlatePosition: null,
    destinationWellID: "",
  });

  return worklist;
};

export const getHarvestWells = (externalThawState: ExternalThawToolState) => {
  return externalThawState.worklistValues.harvestWells
    .map((row) => ({
      task: "HarvestWells",
      transferVol: row?.transferVol?.toString() ?? "",
      sourcePlateType:
        externalThawState.sourcePlateInfo[row.sourcePlateIndex].labwareTypeCode,
      sourcePlatePosition: row.sourcePlateIndex,
      sourceWellID: row.sourceWellId,
      destinationPlateType:
        externalThawState.intPlateInfo[row.destPlateIndex].labwareTypeCode,
      destinationPlatePosition: row.destPlateIndex,
      destinationWellID: row.destWellId,
    }))
    .filter((e) => e.destinationPlateType !== "");
};
